import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar, Check, X, Clock, MoreHorizontal, RefreshCw, CalendarClock, Ban } from "lucide-react";
import { usePatientVisitsForTrial, useUpdateVisitStatus } from "@/hooks/usePatientVisits";
import { useAutoBillingTrigger } from "@/hooks/useAutoBillingTrigger";
import { RescheduleVisitDialog } from "@/components/visit-management/RescheduleVisitDialog";
import { MissedVisitDialog } from "@/components/visit-management/MissedVisitDialog";
import { CancelVisitDialog } from "@/components/visit-management/CancelVisitDialog";
import { format, isToday, isPast, isFuture } from "date-fns";

interface PatientVisitsDisplayProps {
  trialId: string;
  trialSiteId: string;
}

export function PatientVisitsDisplay({ trialId, trialSiteId }: PatientVisitsDisplayProps) {
  const { data: visits = [], isLoading } = usePatientVisitsForTrial(trialId, trialSiteId);
  const { mutate: updateVisitStatus, isPending: isUpdating } = useUpdateVisitStatus();
  const { triggerAutoBilling } = useAutoBillingTrigger();
  const [updatingVisitId, setUpdatingVisitId] = useState<string | null>(null);
  const [rescheduleVisit, setRescheduleVisit] = useState<any | null>(null);
  const [missedVisit, setMissedVisit] = useState<any | null>(null);
  const [cancelVisit, setCancelVisit] = useState<any | null>(null);
  const getStatusBadge = (status: string, scheduledDate: string) => {
    const date = new Date(scheduledDate);
    
    if (status === 'completed') {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    }
    if (status === 'missed') {
      return <Badge className="bg-red-100 text-red-800">Missed</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge className="bg-gray-100 text-gray-800">Cancelled</Badge>;
    }
    if (status === 'rescheduled') {
      return <Badge className="bg-purple-100 text-purple-800">Rescheduled</Badge>;
    }
    
    // For scheduled visits
    if (isToday(date)) {
      return <Badge className="bg-blue-100 text-blue-800">Today</Badge>;
    }
    if (isPast(date)) {
      return <Badge className="bg-yellow-100 text-yellow-800">Overdue</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-800">Scheduled</Badge>;
  };

  const handleMarkCompleted = async (visit: any) => {
    setUpdatingVisitId(visit.id);
    
    updateVisitStatus({
      visitId: visit.id,
      status: 'completed',
      actualDate: new Date().toISOString(),
    }, {
      onSuccess: async () => {
        // Trigger auto-billing for completed visit
        const visitTypeTrigger = `${visit.visit_type}_visit`;
        await triggerAutoBilling({
          trial_id: trialId,
          trial_site_id: trialSiteId,
          patient_id: visit.patient_id,
          event_trigger_type: visitTypeTrigger,
          visit_id: visit.id,
          notes: `Visit ${visit.visit_type} completed`,
        });
        setUpdatingVisitId(null);
      },
      onError: () => setUpdatingVisitId(null),
    });
  };

  const handleMarkMissed = (visit: any) => {
    setMissedVisit(visit);
  };

  const handleReschedule = (visit: any) => {
    setRescheduleVisit(visit);
  };

  const handleCancel = (visit: any) => {
    setCancelVisit(visit);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center text-muted-foreground">
            <Clock className="h-4 w-4 mr-2 animate-spin" />
            Loading visits...
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group visits by patient
  const visitsByPatient = visits.reduce((acc: Record<string, any[]>, visit: any) => {
    const patientId = visit.patient_id;
    if (!acc[patientId]) {
      acc[patientId] = [];
    }
    acc[patientId].push(visit);
    return acc;
  }, {});

  const upcomingVisits = visits.filter((v: any) => 
    v.visit_status === 'scheduled' && isFuture(new Date(v.scheduled_date))
  );
  const todayVisits = visits.filter((v: any) => 
    v.visit_status === 'scheduled' && isToday(new Date(v.scheduled_date))
  );
  const overdueVisits = visits.filter((v: any) => 
    v.visit_status === 'scheduled' && isPast(new Date(v.scheduled_date)) && !isToday(new Date(v.scheduled_date))
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Scheduled Visits
          <div className="flex gap-2 ml-4">
            {todayVisits.length > 0 && (
              <Badge variant="outline" className="bg-blue-50">
                {todayVisits.length} Today
              </Badge>
            )}
            {overdueVisits.length > 0 && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                {overdueVisits.length} Overdue
              </Badge>
            )}
            {upcomingVisits.length > 0 && (
              <Badge variant="outline">
                {upcomingVisits.length} Upcoming
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {visits.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No visits scheduled. Visits are auto-generated when patients are assigned to the trial.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Visit Type</TableHead>
                  <TableHead>Scheduled Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visits.map((visit: any) => (
                  <TableRow key={visit.id}>
                    <TableCell className="font-medium">
                      {visit.profiles?.first_name} {visit.profiles?.last_name}
                      <div className="text-xs text-muted-foreground">
                        {visit.profiles?.patient_number || visit.patient_id.substring(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">
                        {visit.visit_type.replace(/_/g, ' ')}
                      </span>
                      <div className="text-xs text-muted-foreground">
                        Visit #{visit.visit_number}
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(visit.scheduled_date), 'MMM d, yyyy')}
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(visit.scheduled_date), 'h:mm a')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(visit.visit_status, visit.scheduled_date)}
                    </TableCell>
                    <TableCell>
                      {visit.visit_status === 'scheduled' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              disabled={isUpdating && updatingVisitId === visit.id}
                            >
                              {isUpdating && updatingVisitId === visit.id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <MoreHorizontal className="h-4 w-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleMarkCompleted(visit)}>
                              <Check className="h-4 w-4 mr-2 text-green-600" />
                              Mark Completed
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleReschedule(visit)}>
                              <CalendarClock className="h-4 w-4 mr-2 text-yellow-600" />
                              Reschedule
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleMarkMissed(visit)}>
                              <X className="h-4 w-4 mr-2 text-red-600" />
                              Mark Missed
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCancel(visit)}>
                              <Ban className="h-4 w-4 mr-2 text-muted-foreground" />
                              Cancel Visit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {visit.visit_status === 'completed' && (
                        <span className="text-xs text-muted-foreground">
                          {visit.actual_date && format(new Date(visit.actual_date), 'MMM d')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {rescheduleVisit && (
        <RescheduleVisitDialog
          open={!!rescheduleVisit}
          onOpenChange={(open) => !open && setRescheduleVisit(null)}
          visit={rescheduleVisit}
          onSuccess={() => setRescheduleVisit(null)}
        />
      )}

      {missedVisit && (
        <MissedVisitDialog
          open={!!missedVisit}
          onOpenChange={(open) => !open && setMissedVisit(null)}
          visit={missedVisit}
          onSuccess={() => setMissedVisit(null)}
        />
      )}

      {cancelVisit && (
        <CancelVisitDialog
          open={!!cancelVisit}
          onOpenChange={(open) => !open && setCancelVisit(null)}
          visit={cancelVisit}
          onSuccess={() => setCancelVisit(null)}
        />
      )}
    </Card>
  );
}
